const NEWSLETTER_SUBSCRIBED_COOKIE = 'aesthet_newsletter_subscribed';
const NEWSLETTER_COOKIE_DAYS = 3650;
const FOOTER_NEWSLETTER_SCROLL_KEY = 'aesthet_footer_newsletter_scroll';
const FOOTER_NEWSLETTER_FORM_ID = 'footer_newsletter_signup';

/**
 * Sets a cookie.
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
function setNewsletterCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * @param {string} name
 * @returns {?string}
 */
function getNewsletterCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

function isLoggedInNewsletterCustomer() {
  return document.body?.getAttribute('data-customer-logged-in') === 'true';
}

function isGuestNewsletterSubscribed() {
  if (getNewsletterCookie(NEWSLETTER_SUBSCRIBED_COOKIE)) {
    return true;
  }

  const popups = document.querySelectorAll('pop-up');
  for (const popup of popups) {
    if (getNewsletterCookie(`${popup.id}-subscribed`)) {
      return true;
    }
  }

  return false;
}

function markGuestNewsletterSubscribed() {
  if (!isLoggedInNewsletterCustomer()) {
    setNewsletterCookie(NEWSLETTER_SUBSCRIBED_COOKIE, '1', NEWSLETTER_COOKIE_DAYS);
  }
}

function hideFooterNewsletterForGuestSubscriber(wrapper) {
  if (isLoggedInNewsletterCustomer() || !isGuestNewsletterSubscribed()) {
    return;
  }

  const formFields = wrapper.querySelector('[data-newsletter-form-fields]');
  const message = wrapper.querySelector('[data-newsletter-already-subscribed]');

  if (!formFields || !message) {
    return;
  }

  formFields.hidden = true;
  message.hidden = false;
}

function persistNewsletterSuccessFromForms() {
  document.querySelectorAll('[data-newsletter-signup]').forEach((wrapper) => {
    const success = wrapper.querySelector(
      '.signup-form__response--success:not(.signup-form__response--already-subscribed):not([hidden])'
    );

    if (success) {
      markGuestNewsletterSubscribed();
    }
  });
}

function isCustomerPosted() {
  try {
    return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
  } catch (e) {
    return window.location.search.indexOf('customer_posted=true') !== -1;
  }
}

function consumeFooterNewsletterScrollFlag() {
  try {
    const flagged = sessionStorage.getItem(FOOTER_NEWSLETTER_SCROLL_KEY) === '1';
    sessionStorage.removeItem(FOOTER_NEWSLETTER_SCROLL_KEY);
    return flagged;
  } catch (e) {
    return false;
  }
}

function scrollToFooterNewsletter() {
  const target = document.getElementById(FOOTER_NEWSLETTER_FORM_ID)
    || document.querySelector('[data-footer-newsletter]')
    || document.querySelector('.section-footer__newsletter-block');

  if (!target) {
    return;
  }

  window.scrollTo({
    top: target.getBoundingClientRect().top + window.pageYOffset - 24,
    left: 0,
    behavior: 'auto'
  });
}

function scrollToFooterNewsletterIfNeeded() {
  if (window.location.pathname.indexOf('/challenge') !== -1) {
    return;
  }

  const wrapper = document.querySelector('[data-newsletter-signup][data-footer-newsletter]');
  const success = wrapper?.querySelector(
    '.signup-form__response--success:not(.signup-form__response--already-subscribed):not([hidden])'
  );
  const flagged = consumeFooterNewsletterScrollFlag();

  if (!success && !isCustomerPosted() && !flagged) {
    return;
  }

  scrollToFooterNewsletter();
}

const NEWSLETTER_LANGUAGE_NAMES = {
  el: 'Greek',
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  nl: 'Dutch',
  pt: 'Portuguese',
  ja: 'Japanese'
};

function localizeNewsletterFormAction(form) {
  const wrapper = form.closest('[data-newsletter-root]');
  const root = wrapper && wrapper.getAttribute('data-newsletter-root');
  if (!root || root === '/') {
    return;
  }

  const action = form.getAttribute('action') || '';
  const hashAt = action.indexOf('#');
  const path = hashAt === -1 ? action : action.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : action.slice(hashAt);
  const prefix = root.endsWith('/') ? root.slice(0, -1) : root;

  if (!path.startsWith('/contact') || path.startsWith(prefix + '/contact')) {
    return;
  }

  form.setAttribute('action', prefix + path + hash);
}

function klaviyoCompanyId() {
  const script = document.querySelector('script[src*="klaviyo.js"]');
  if (!script) {
    return '';
  }

  try {
    return new URL(script.src).searchParams.get('company_id') || '';
  } catch (e) {
    const match = String(script.src).match(/company_id=([A-Za-z0-9]+)/);
    return match ? match[1] : '';
  }
}

/**
 * Write the three values the welcome-flow split already checks.
 * The Shopify reload cancels a normal identify call, so this also posts
 * with keepalive and the submit waits until that request settles.
 * @param {HTMLFormElement} form
 * @returns {Promise<void>}
 */
function identifyNewsletterLocale(form) {
  const wrapper = form.closest('[data-newsletter-locale]');
  if (!wrapper) {
    return Promise.resolve();
  }

  const lang = (wrapper.getAttribute('data-newsletter-lang') || '').toLowerCase();
  const languageCode = lang.split('-')[0];
  const email = form.querySelector('input[name="contact[email]"]')?.value?.trim();
  const languageName = NEWSLETTER_LANGUAGE_NAMES[languageCode];

  if (!email || !languageCode) {
    return Promise.resolve();
  }

  const profile = {
    email,
    $email: email,
    $locale: languageCode,
    'Language (Orbe)': languageCode
  };

  if (languageName) {
    profile.Language = languageName;
  }

  const tasks = [];
  const companyId = klaviyoCompanyId();

  if (companyId) {
    const attributes = {
      email,
      locale: languageCode,
      properties: {
        'Language (Orbe)': languageCode
      }
    };

    if (languageName) {
      attributes.properties.Language = languageName;
    }

    tasks.push(fetch(`https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/vnd.api+json',
        revision: '2024-10-15'
      },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes
        }
      }),
      keepalive: true,
      credentials: 'omit',
      mode: 'cors'
    }).catch(() => {}));
  }

  try {
    const klaviyo = window.klaviyo;
    if (klaviyo && typeof klaviyo.identify === 'function') {
      const pending = klaviyo.identify(profile);
      if (pending && typeof pending.then === 'function') {
        tasks.push(Promise.resolve(pending).catch(() => {}));
      }
    } else {
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', profile]);
    }
  } catch (e) {
    // The customer tag is still posted with the Shopify form.
  }

  return Promise.all(tasks).then(() => {});
}

function markNewsletterLocale(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  if (form.dataset.newsletterLocaleSent === '1') {
    return;
  }

  if (!form.querySelector('input[name="contact[email]"]') || !form.closest('[data-newsletter-locale]')) {
    return;
  }

  localizeNewsletterFormAction(form);
  event.preventDefault();
  form.dataset.newsletterLocaleSent = '1';

  let finished = false;
  const submitForm = () => {
    if (finished) {
      return;
    }
    finished = true;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  };

  setTimeout(submitForm, 1500);
  identifyNewsletterLocale(form).then(submitForm, submitForm);
}

function markFooterNewsletterScrollIntent(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  if (form.id !== FOOTER_NEWSLETTER_FORM_ID && !form.closest('[data-footer-newsletter]')) {
    return;
  }

  try {
    sessionStorage.setItem(FOOTER_NEWSLETTER_SCROLL_KEY, '1');
  } catch (e) {
    // ignore
  }
}

function initNewsletterSignupState() {
  persistNewsletterSuccessFromForms();

  document.querySelectorAll('[data-newsletter-signup][data-hide-if-subscribed]').forEach((wrapper) => {
    hideFooterNewsletterForGuestSubscriber(wrapper);
  });

  scrollToFooterNewsletterIfNeeded();
}

if (!window.__aesthetNewsletterSignupInit) {
  window.__aesthetNewsletterSignupInit = true;

  document.addEventListener('submit', markFooterNewsletterScrollIntent, true);
  document.addEventListener('submit', markNewsletterLocale, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNewsletterSignupState);
  } else {
    initNewsletterSignupState();
  }
}
